/**
 * Whether a section of a built-in page is shown.
 *
 * The pages that were built by hand - home, organisation, mitmachen,
 * podcast - have a fixed set of sections. Removing one used to mean emptying
 * its text, and the text was then gone. Instead every such section carries a
 * `visible` flag that the editing interface shows as a checkbox: the copy stays
 * in the content file and only the rendering stops.
 *
 * Absent means visible. That keeps the flag optional for anything added later
 * and, more importantly, means a section can never disappear because a save
 * dropped the key.
 */
export const isVisible = (section?: { visible?: boolean } | null): boolean =>
  section != null && section.visible !== false;

/**
 * The order a built-in page renders its sections in.
 *
 * `declared` is the order the page was designed in, `chosen` the order an
 * editor dragged them into. The chosen one wins, but it is treated as a wish
 * rather than as the truth, because it is hand-edited data that outlives the
 * code it refers to:
 *
 *  - a name that is not a section of this page is ignored (renamed, removed);
 *  - a section the list does not mention is appended, in its designed order,
 *    rather than dropped - a section nobody can see is worse than one in an
 *    unexpected place, and it would be invisible in the editing interface too;
 *  - a name listed twice is rendered once.
 *
 * The result therefore always contains every declared section exactly once,
 * whatever the list says.
 */
export const orderSections = (declared: string[], chosen?: readonly string[] | null): string[] => {
  const ordered: string[] = [];

  (Array.isArray(chosen) ? chosen : []).forEach((key) => {
    if (declared.includes(key) && !ordered.includes(key)) {
      ordered.push(key);
    }
  });

  declared.forEach((key) => {
    if (!ordered.includes(key)) {
      ordered.push(key);
    }
  });

  return ordered;
};
